// ============================================================================
// File: NotificationBell.tsx (extended — Spec 160, US5)
// Added: 2026-05-31 — role gate (hidden for Viewer), useAlerts integration
// Author: Peter Dobler (Dobler Data Solutions)
// Constitution: v1.15.0 | Principle XXXVII
// ============================================================================
import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationDropdown } from './NotificationDropdown';
import { useAlerts } from '../../hooks/useAlerts';
import { useAuth } from '../../modules/backup/hooks/useAuth';

interface NotificationBellProps {
  /** Whether notifications are enabled */
  enabled?: boolean;
  /** Custom poll interval in milliseconds */
  pollInterval?: number;
  /** Custom CSS classes */
  className?: string;
}

/**
 * NotificationBell component with badge and dropdown
 * 
 * Features:
 * - Bell icon with unread count badge
 * - Click to open/close dropdown
 * - Keyboard navigation support
 * - 99+ display for high counts
 * - Auto-close on outside click
 * - Proper ARIA attributes
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({
  enabled = true,
  pollInterval,
  className = '',
}) => {
  // All hooks must be called unconditionally (Rules of Hooks)
  const { user } = useAuth();
  const role = user?.role ?? '';
  const isViewer = role.toLowerCase() === 'viewer';

  // Spec 160: pull alert unread count from the new 20s-polling hook
  // useAlerts internally returns empty state for Viewer — safe to call unconditionally
  const { unacknowledgedCount: alertCount, acknowledge: acknowledgeAlerts } = useAlerts();

  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    badgeCount,
    totalUnacknowledged,
    isLoading,
    error,
    markAcknowledgedLocal,
    removeNotification,
    refresh,
    lastFetch,
  } = useNotifications({
    enabled,
    pollInterval,
  });

  // Spec 255: acknowledging from the bell is a DELIBERATE ownership action (the explicit
  // Acknowledge button), never a side-effect of clicking to read. It persists via
  // useAlerts.acknowledge (POST /api/alerts/acknowledge → dbo.alerts: is_acknowledged,
  // acknowledged_by, acknowledged_at) and moves the dropdown + badge in lock-step.
  const handleAcknowledge = (id: string): void => {
    markAcknowledgedLocal(id); // instant: row flips to acknowledged, button disappears
    // Persist (POST → dbo.alerts), then refresh so the exact "by <who>" attribution + the
    // badge count reconcile with the server truth.
    void acknowledgeAlerts([id]).then(() => refresh());
  };

  /**
   * Toggle dropdown visibility
   */
  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  /**
   * Close dropdown
   */
  const closeDropdown = () => {
    setIsOpen(false);
  };

  /**
   * Handle outside click to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeDropdown();
      bellRef.current?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown();
    }
  };

  /**
   * Get badge styling based on state
   */
  // Spec 160: use alertCount (from useAlerts) as the primary badge count
  const effectiveBadgeCount = alertCount > 0 ? alertCount : (unreadCount ?? 0);

  const getBadgeStyles = () => {
    if (effectiveBadgeCount === 0) return 'hidden';

    const baseStyles = 'absolute -top-1 -right-1 flex items-center justify-center text-xs font-bold text-white rounded-full min-w-[18px] h-[18px] px-1';

    if (effectiveBadgeCount > 10) {
      return `${baseStyles} bg-red-600 shadow-lg`;
    } else if (effectiveBadgeCount > 5) {
      return `${baseStyles} bg-orange-500 shadow-md`;
    } else {
      return `${baseStyles} bg-blue-600 shadow-md`;
    }
  };

  /**
   * Get bell button styling based on state
   */
  const getButtonStyles = () => {
    const baseStyles = 'relative p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1';

    if (isOpen) {
      return `${baseStyles} bg-[var(--color-bg-subtle)] text-[var(--color-text-primary)]`;
    }

    if (unreadCount > 0) {
      return `${baseStyles} text-[var(--color-accent)] hover:opacity-90`;
    }

    return baseStyles;
  };

  /**
   * Get accessibility attributes
   */
  const getAriaAttributes = () => {
    return {
      'aria-label': `Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`,
      'aria-expanded': isOpen,
      'aria-haspopup': 'true' as const,
      'aria-describedby': error ? 'notification-error' : undefined,
    };
  };

  // Spec 160 US5: Viewer role — must not render (guard after all hooks)
  if (isViewer) return null;

  if (!enabled) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        className={getButtonStyles()}
        disabled={error !== null}
        {...getAriaAttributes()}
      >
        {/* Bell Icon */}
        <Bell 
          className={`w-5 h-5 ${isLoading ? 'animate-pulse' : ''}`}
          aria-hidden="true"
        />
        
        {/* Badge — Spec 160: shows alert count from 20s poll */}
        <span className={getBadgeStyles()}>
          {effectiveBadgeCount > 99 ? '99+' : effectiveBadgeCount}
        </span>
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full opacity-25 animate-ping" />
          </div>
        )}
      </button>

      {/* Error state */}
      {error && (
        <div 
          id="notification-error"
          className="absolute top-full right-0 mt-2 p-2 bg-red-100 border border-red-300 text-red-700 text-xs rounded shadow-lg z-50 min-w-[200px]"
          role="alert"
        >
          <p className="font-medium">Failed to load notifications</p>
          <p className="text-red-600">{error}</p>
          <button
            onClick={refresh}
            className="mt-1 text-red-800 underline hover:no-underline text-xs"
          >
            Try again
          </button>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && !error && (
        <NotificationDropdown
          ref={dropdownRef}
          notifications={notifications}
          unreadCount={unreadCount}
          totalUnacknowledged={totalUnacknowledged}
          isLoading={isLoading}
          lastFetch={lastFetch}
          onClose={closeDropdown}
          onAcknowledge={handleAcknowledge}
          onRemoveNotification={removeNotification}
          onRefresh={refresh}
        />
      )}
    </div>
  );
};

export default NotificationBell;